'use client';

import { ChangeEvent, InputHTMLAttributes } from 'react';
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/atoms/form';
import { Input } from '@/components/atoms/input';
import { Control, FieldValues, Path } from 'react-hook-form';

interface FormattedInputProps<TFieldValues extends FieldValues>
  extends InputHTMLAttributes<HTMLInputElement> {
  control: Control<TFieldValues>;
  name: Path<TFieldValues>;
  formatter?: (value: string) => string;
  placeholder?: string;
  maxLength?: number;
  className?: string;
  converter?: (value: string) => number;
}

export function FormattedInput<TFieldValues extends FieldValues>({
  control,
  name,
  formatter,
  placeholder,
  maxLength,
  className,
  converter,
  ...rest
}: FormattedInputProps<TFieldValues>) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        const formattedValue =
          formatter && field.value
            ? formatter(field.value || '')
            : field.value || '';

        return (
          <FormItem className="flex-1">
            <FormControl>
              <Input
                placeholder={placeholder}
                {...field}
                {...rest}
                value={formattedValue}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  if (converter) {
                    const converted = converter(e.target.value);
                    field.onChange(converted);
                    return;
                  }
                  if (formatter) {
                    const formatted = formatter(e.target.value);
                    field.onChange(formatted);
                  } else {
                    field.onChange(e);
                  }
                }}
                maxLength={maxLength}
                className={`rounded-md h-[50px] px-[20px] py-[16px] data-[placeholder]:text-opacity-40 placeholder:text-opacity-40 border-gray-200 ${className}`}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}
